:host {
  /* When the host has an explicit inline-size set by the consumer
     (e.g. inline-size: 100% inside a constrained wrapper), the control
     fills it and the label truncates with ellipsis. Without an explicit
     width, the host sizes to its intrinsic content. */
  /* Size axis defaults (md) */
  --_height: var(--chip-container-height-md);
  --_touch-target: var(--chip-container-touch-target-md);
  --_min-width: var(--chip-container-min-width-md);
  --_padding-inline: var(--chip-container-padding-inline-md);
  --_padding-inline-icon: var(--chip-container-padding-inline-icon-md);
  --_padding-inline-remove: var(--chip-container-padding-inline-remove-md);
  --_gap: var(--chip-container-gap-md);
  --_gap-icon: var(--chip-container-gap-icon-md);
  --_font-size: var(--chip-label-font-size-md);
  --_line-height: var(--chip-label-line-height-md);
  --_icon-size: var(--chip-icon-size-md);
  --_remove-size: var(--chip-input-remove-size-md);
  --_remove-icon-size: var(--chip-input-remove-icon-size-md);
  --_avatar-size: var(--chip-avatar-size-md);
  --_count-size: var(--chip-count-size-md);
  --_count-font-size: var(--chip-count-font-size-md);
  --_count-padding-inline: var(--chip-count-padding-inline-md);

  /* Type axis defaults (filter, off) */
  --_bg: var(--chip-filter-background-default);
  --_bg-hover: var(--chip-filter-background-hover);
  --_label-color: var(--chip-filter-label-default);
  --_icon-color: var(--chip-filter-icon-default);
  --_remove-bg: var(--chip-input-remove-background-default);
  --_remove-bg-hover: var(--chip-input-remove-background-hover);
  --_remove-color: var(--chip-input-remove-color-default);

  display: inline-flex;
  vertical-align: middle;
  position: relative;
  border-radius: var(--chip-container-border-radius);
  max-inline-size: 100%;
}

:host .control {
  inline-size: 100%;
}

/* ---------- Size axis ---------- */

:host([size='sm']) {
  --_height: var(--chip-container-height-sm);
  --_touch-target: var(--chip-container-touch-target-sm);
  --_min-width: var(--chip-container-min-width-sm);
  --_padding-inline: var(--chip-container-padding-inline-sm);
  --_padding-inline-icon: var(--chip-container-padding-inline-icon-sm);
  --_padding-inline-remove: var(--chip-container-padding-inline-remove-sm);
  --_gap: var(--chip-container-gap-sm);
  --_gap-icon: var(--chip-container-gap-icon-sm);
  --_font-size: var(--chip-label-font-size-sm);
  --_line-height: var(--chip-label-line-height-sm);
  --_icon-size: var(--chip-icon-size-sm);
  --_remove-size: var(--chip-input-remove-size-sm);
  --_remove-icon-size: var(--chip-input-remove-icon-size-sm);
  --_avatar-size: var(--chip-avatar-size-sm);
  --_count-size: var(--chip-count-size-sm);
  --_count-font-size: var(--chip-count-font-size-sm);
  --_count-padding-inline: var(--chip-count-padding-inline-sm);
}

/* ---------- Type axis ---------- */

:host([type='filter']) {
  --_bg: var(--chip-filter-background-default);
  --_bg-hover: var(--chip-filter-background-hover);
  --_label-color: var(--chip-filter-label-default);
  --_icon-color: var(--chip-filter-icon-default);
}

:host([type='filter'][selected]) {
  --_bg: var(--chip-filter-background-selected);
  --_bg-hover: var(--chip-filter-background-selected-hover);
  --_label-color: var(--chip-filter-label-selected);
  --_icon-color: var(--chip-filter-icon-selected);
}

:host([type='input']) {
  --_bg: var(--chip-input-background-default);
  --_bg-hover: var(--chip-input-background-hover);
  --_label-color: var(--chip-input-label-default);
  --_icon-color: var(--chip-input-icon-default);
  /* Input chips get a slightly wider floor than filter chips (Figma spec). */
  --_min-width: var(--chip-input-min-width-md);
}

/* 2-attribute selector beats the single-attribute size axis so the sm input
   floor isn't clobbered by the generic sm min-width. */
:host([type='input'][size='sm']) {
  --_min-width: var(--chip-input-min-width-sm);
}

/* ---------- Internal main control ---------- */

.control {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--_gap);

  block-size: var(--_height);
  min-inline-size: var(--_min-width);
  padding-block: 0;
  padding-inline: var(--_padding-inline);

  box-sizing: border-box;
  border: 0;
  border-radius: var(--chip-container-border-radius);
  background-color: var(--_bg);
  color: var(--_label-color);

  font-family: var(--chip-label-font-family);
  font-size: var(--_font-size);
  line-height: var(--_line-height);
  font-weight: var(--chip-label-font-weight);
  text-align: center;
  text-decoration: none;

  cursor: pointer;
  user-select: none;
  appearance: none;
  -webkit-appearance: none;

  transition:
    background-color var(--chip-container-transition-duration) var(--chip-container-transition-timing-function),
    color var(--chip-container-transition-duration) var(--chip-container-transition-timing-function),
    box-shadow var(--chip-container-transition-duration) var(--chip-container-transition-timing-function);
}

/* When the chip is removable, the control's trailing radius and padding collapse
   so the trailing remove button reads as part of the same surface. */
:host([type='input'][removable]) .control {
  border-start-end-radius: 0;
  border-end-end-radius: 0;
  padding-inline-end: var(--_gap-icon);
}

:host([type='input'][removable]) .remove {
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}

/* Label — handles ellipsis truncation */
.label {
  display: inline-flex;
  align-items: center;
  min-inline-size: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.label:empty {
  display: none;
}

/* Hover — disabled when chip is disabled */
:host(:not([disabled])) .control:hover {
  background-color: var(--_bg-hover);
}

/* Asymmetric padding when a leading icon / auto-check is present */
:host(.has-icon-start) .control,
:host(.shows-check) .control {
  padding-inline-start: var(--_padding-inline-icon);
}

/* Avatar hugs the leading edge: inset symmetrically so the circle is centred
   within the pill's rounded corner. */
:host(.has-avatar) .control {
  padding-inline-start: calc((var(--_height) - var(--_avatar-size)) / 2);
}

/* ---------- Focus ring (dual halo, same as mud-button) ---------- */

.control:focus-visible,
.remove:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 var(--focus-ring-width-inner) var(--focus-ring-color-inner),
    0 0 0 calc(var(--focus-ring-width-inner) + var(--focus-ring-width-outer)) var(--focus-ring-color-outer);
  position: relative;
  z-index: 1;
}

/* ---------- Remove button (input type only) ---------- */

.remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  block-size: var(--_height);
  inline-size: calc(var(--_remove-size) + var(--_padding-inline-remove));
  padding-inline-start: 0;
  padding-inline-end: var(--_padding-inline-remove);
  margin-inline-start: 0;

  border: 0;
  border-radius: var(--chip-container-border-radius);
  background-color: var(--_bg);
  color: var(--_remove-color);

  cursor: pointer;
  user-select: none;
  appearance: none;
  -webkit-appearance: none;

  transition:
    background-color var(--chip-container-transition-duration) var(--chip-container-transition-timing-function),
    color var(--chip-container-transition-duration) var(--chip-container-transition-timing-function),
    box-shadow var(--chip-container-transition-duration) var(--chip-container-transition-timing-function);
}

/* Hover the chip → also tints the remove background to match the control. */
:host(:not([disabled])) .control:hover ~ .remove {
  background-color: var(--_bg-hover);
}

/* Hover the remove pill itself → inner circle gets a darker fill. */
.remove-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  block-size: var(--_remove-size);
  inline-size: var(--_remove-size);
  border-radius: var(--chip-input-remove-border-radius);
  background-color: transparent;
  transition: background-color var(--chip-container-transition-duration)
    var(--chip-container-transition-timing-function);
}

:host(:not([disabled])) .remove:hover .remove-icon {
  background-color: var(--_remove-bg-hover);
}

.remove-icon svg {
  display: block;
  inline-size: calc(var(--_remove-icon-size) * 0.5);
  block-size: calc(var(--_remove-icon-size) * 0.5);
  pointer-events: none;
}

/* ---------- Slotted leading icon / avatar ---------- */

::slotted([slot='icon-start']) {
  flex-shrink: 0;
  display: inline-flex;
  inline-size: var(--_icon-size);
  block-size: var(--_icon-size);
  color: var(--_icon-color);
}

::slotted(mud-icon[slot='icon-start']) {
  --icon-color: currentcolor;
}

/* ---------- Leading avatar (flush, ~chip height) ---------- */

.avatar {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

:host(:not(.has-avatar)) .avatar {
  display: none;
}

::slotted([slot='avatar']) {
  flex-shrink: 0;
  inline-size: var(--_avatar-size);
  block-size: var(--_avatar-size);
  border-radius: var(--border-radius-full, 9999px);
  object-fit: cover;
  overflow: hidden;
}

/* ---------- Auto check (multi-selection, selected) ---------- */

.check {
  flex-shrink: 0;
  --icon-color: currentcolor;
  color: var(--_label-color);
}

/* ---------- Numbered badge (count) ---------- */

.count {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-inline-size: var(--_count-size);
  block-size: var(--_count-size);
  padding-inline: var(--_count-padding-inline);
  border-radius: var(--border-radius-full, 9999px);
  /* Inverts against the chip surface so it stays legible default + selected. */
  background-color: var(--_label-color);
  color: var(--_bg);
  font-family: var(--chip-label-font-family);
  font-size: var(--_count-font-size);
  font-weight: var(--chip-label-font-weight);
  line-height: 1;
}

:host([disabled]) .count {
  background-color: var(--chip-disabled-label);
  color: var(--chip-disabled-background);
}

/* ---------- Disabled ---------- */

:host([disabled]) .control,
:host([disabled]) .remove {
  background-color: var(--chip-disabled-background);
  color: var(--chip-disabled-label);
  cursor: not-allowed;
  pointer-events: none;
}

:host([disabled]) .remove {
  color: var(--chip-input-remove-color-disabled);
}

:host([disabled]) ::slotted([slot='icon-start']) {
  color: var(--chip-disabled-icon);
}

/* ---------- Reduced motion ---------- */

@media (prefers-reduced-motion: reduce) {
  .control,
  .remove,
  .remove-icon {
    transition: none;
  }
}

/* ---------- Touch target expansion on coarse pointers ---------- */

@media (pointer: coarse) {
  .control::before {
    content: '';
    position: absolute;
    inset: calc((var(--_height) - var(--_touch-target)) / 2);
  }
}
